import React, { useState, useEffect } from "react";
import BookingCalendarView from "./BookingCalendarView";
import { getFirstImageUrl } from "../../utils/imageUtils";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import jsPDF from "jspdf";
import styles from "./BookingManagement.module.css";

const months = [
  "All",
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const monthMap = {
  January: "Jan",
  February: "Feb",
  March: "Mar",
  April: "Apr",
  May: "May",
  June: "Jun",
  July: "Jul",
  August: "Aug",
  September: "Sep",
  October: "Oct",
  November: "Nov",
  December: "Dec",
};

const BookingManagement = () => {
  const navigate = useNavigate();
  const [reservationsData, setReservationsData] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [month, setMonth] = useState("All");
  const [sort, setSort] = useState("Most Recent");
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 4;

  // Manual booking modal state
  const [showManualModal, setShowManualModal] = useState(false);
  const [manualBooking, setManualBooking] = useState({
    room_id: "",
    room_type_id: "",
    start_date: "",
    end_date: "",
    guest_first_name: "",
    guest_last_name: "",
    guest_email: "",
    guest_phone: "",
    breakfast: false,
    breakfast_count: 1,
    airport_direction: "none",
    pickup_flight_number: "",
    pickup_airline: "",
    pickup_arrival_time: "",
    pickup_terminal: "",
    dropoff_flight_number: "",
    dropoff_airline: "",
    dropoff_departure_time: "",
    dropoff_terminal: "",
    dropoff_pickup_time: "",
    affiliate_code: "",
    discount_type: "fixed",
    discount_amount: "",
    discount_reason: "",
  });
  const [manualError, setManualError] = useState("");
  const [manualSuccess, setManualSuccess] = useState("");
  const [roomOptions, setRoomOptions] = useState([]);

  useEffect(() => {
    api.get("/bookings").then((res) => {
      setReservationsData(res.data.bookings || []);
    });
  }, []);

  const [roomTypeOptions, setRoomTypeOptions] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    api.get("/rooms").then((res) => {
      setRoomTypeOptions(res.data.rooms || res.data);
    });
  }, []);

  const filteredData = reservationsData
    .filter((r) => {
      const matchesSearch =
        r.roomID.toString().toLowerCase().includes(search.toLowerCase()) ||
        r.roomType.toLowerCase().includes(search.toLowerCase()) ||
        (r.guest_email && r.guest_email.toLowerCase().includes(search.toLowerCase()));

      const matchesMonth =
        month === "All"
          ? true
          : r.stayDates
              .toLowerCase()
              .includes((monthMap[month] || "").toLowerCase());

      return matchesSearch && matchesMonth;
    })
    .sort((a, b) => {
      if (sort === "Most Recent") return b.id - a.id;
      if (sort === "Oldest") return a.id - b.id;
      return 0;
    });

  const paginated = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  const handleView = (id) => {
    navigate(`/admin/bookings/details/${id}`);
  };

  const handleManualBooking = async () => {
    setManualError("");
    setManualSuccess("");
    try {
      const payload = { ...manualBooking };
      
      // Ensure room_type_id is set based on selected room
      if (!payload.room_type_id && payload.room_id) {
        const selectedRoom = roomOptions.find(r => r.id.toString() === payload.room_id.toString());
        if (selectedRoom && selectedRoom.room_type_id) {
          payload.room_type_id = selectedRoom.room_type_id;
        }
      }

      // Build airport conveyance object
      const airportConveyance = payload.airport_direction !== 'none' ? {
        direction: payload.airport_direction,
        pickup: payload.airport_direction === 'pickup' || payload.airport_direction === 'both' ? {
          flight_number: payload.pickup_flight_number,
          arrival_time: payload.pickup_arrival_time,
          airline: payload.pickup_airline,
          terminal: payload.pickup_terminal,
        } : null,
        dropoff: payload.airport_direction === 'dropoff' || payload.airport_direction === 'both' ? {
          flight_number: payload.dropoff_flight_number,
          departure_time: payload.dropoff_departure_time,
          airline: payload.dropoff_airline,
          terminal: payload.dropoff_terminal,
          pickup_time: payload.dropoff_pickup_time,
        } : null
      } : null;

      // Clean payload for API
      const apiPayload = {
        room_type_id: payload.room_type_id,
        start_date: payload.start_date,
        end_date: payload.end_date,
        guest_first_name: payload.guest_first_name,
        guest_last_name: payload.guest_last_name,
        guest_email: payload.guest_email,
        guest_phone: payload.guest_phone,
        breakfast: payload.breakfast,
        breakfast_count: payload.breakfast ? parseInt(payload.breakfast_count) : null,
        airport_conveyance: airportConveyance,
        affiliate_code: payload.affiliate_code || null,
        discount_type: payload.discount_amount ? (payload.discount_type || 'fixed') : null,
        discount_amount: payload.discount_amount ? parseFloat(payload.discount_amount) : null,
        discount_reason: payload.discount_reason || null,
      };

      console.log('Manual booking payload:', apiPayload); // Debug log
      const res = await api.post("/bookings/manual", apiPayload);
      const createdBooking = res.data.booking;
      setManualSuccess("Booking created successfully! Downloading receipt…");
      generateManualReceipt(createdBooking);
      setShowManualModal(false);
      setManualBooking({
        room_id: "",
        room_type_id: "",
        start_date: "",
        end_date: "",
        guest_first_name: "",
        guest_last_name: "",
        guest_email: "",
        guest_phone: "",
        breakfast: false,
        breakfast_count: 1,
        airport_direction: "none",
        pickup_flight_number: "",
        pickup_airline: "",
        pickup_arrival_time: "",
        pickup_terminal: "",
        dropoff_flight_number: "",
        dropoff_airline: "",
        dropoff_departure_time: "",
        dropoff_terminal: "",
        dropoff_pickup_time: "",
        affiliate_code: "",
        discount_type: "",
        discount_amount: "",
        discount_reason: "",
      });
      // Optionally refresh bookings
      api.get("/bookings").then((res) => {
        setReservationsData(res.data.bookings || []);
      });
    } catch (err) {
      setManualError(err.response?.data?.message || "Error creating booking.");
    }
  };

  const generateManualReceipt = (booking) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    const NAVY  = [35,  61, 129];
    const GOLD  = [212, 175,  55];
    const WHITE = [255, 255, 255];
    const LIGHT = [247, 248, 250];
    const MUTED = [113, 128, 150];
    const DARK  = [45,  55,  72];
    const GREEN = [39, 174,  96];

    // Header
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 90, "F");
    doc.setFillColor(...GOLD);
    doc.rect(0, 90, W, 4, "F");
    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(22);
    doc.text("AURELIA", 110, 48);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...GOLD);
    doc.text("APARTMENTS", 110, 64);
    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("BOOKING RECEIPT", W - 30, 52, { align: "right" });

    // Meta row
    doc.setFillColor(...LIGHT);
    doc.rect(0, 94, W, 44, "F");
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("RECEIPT NO.", 30, 113);
    doc.text("DATE ISSUED", 200, 113);
    doc.text("STATUS", W - 140, 113);
    doc.setTextColor(...DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    const receiptNo = `AUR-${String(booking.id).padStart(6, "0")}`;
    doc.text(receiptNo, 30, 128);
    doc.text(new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }), 200, 128);
    doc.setTextColor(...GREEN);
    doc.text("CONFIRMED", W - 140, 128);

    // Divider
    doc.setDrawColor(...GOLD);
    doc.setLineWidth(0.5);
    doc.line(30, 152, W - 30, 152);

    // Billed To | Booking Details
    let y = 175;
    const colR = W / 2 + 10;
    const guestName = [booking.guest_first_name, booking.guest_last_name].filter(Boolean).join(" ") || booking.guest_name || "Guest";
    const roomTypeName = booking.room?.roomType?.name || booking.room?.room_type?.name || "";

    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("BILLED TO", 30, y);
    y += 16;
    doc.setTextColor(...DARK);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(guestName, 30, y);
    y += 16;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    if (booking.guest_email) { doc.text(booking.guest_email, 30, y); y += 14; }
    if (booking.guest_phone) { doc.text(booking.guest_phone, 30, y); y += 14; }
    y += 4;
    const badge = "Walk-in / Manual";
    doc.setFillColor(...NAVY);
    doc.roundedRect(30, y, doc.getTextWidth(badge) + 16, 18, 3, 3, "F");
    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(badge, 38, y + 12);

    // Right column
    let yR = 175;
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text("BOOKING DETAILS", colR, yR);
    const nights = booking.start_date && booking.end_date
      ? Math.max(1, Math.round((new Date(booking.end_date) - new Date(booking.start_date)) / 86400000))
      : null;
    const infoRows = [
      ["Room Type",  roomTypeName || "—"],
      ["Check-in",   booking.start_date || "—"],
      ["Check-out",  booking.end_date   || "—"],
      ["Duration",   nights ? `${nights} night${nights !== 1 ? "s" : ""}` : "—"],
      ["Booking Ref",`#BKNG-${booking.id}`],
    ];
    yR += 14;
    infoRows.forEach(([label, val]) => {
      doc.setTextColor(...MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(label, colR, yR);
      doc.setTextColor(...DARK);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text(String(val), colR + 90, yR);
      yR += 14;
    });

    // Payment summary box
    const hasDiscount = booking.discount_amount && Number(booking.discount_amount) > 0;
    const boxY = Math.max(y + 36, yR + 16);
    const boxH = hasDiscount ? 130 : 110;
    doc.setFillColor(...LIGHT);
    doc.roundedRect(30, boxY, W - 60, boxH, 6, 6, "F");
    doc.setDrawColor(220, 225, 235);
    doc.setLineWidth(0.5);
    doc.roundedRect(30, boxY, W - 60, boxH, 6, 6, "S");

    const row = (label, value, rowY) => {
      doc.setTextColor(...MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text(label, 50, rowY);
      doc.setTextColor(...DARK);
      doc.setFont("helvetica", "normal");
      doc.text(value || "—", W - 50, rowY, { align: "right" });
    };

    row("Receipt No.", receiptNo, boxY + 22);
    row("Booking Type", "Walk-in / Manual", boxY + 38);
    row("Guest", guestName, boxY + 54);

    let rowOffset = 54;
    if (hasDiscount) {
      row("Base Amount", `NGN ${Number(booking.base_amount).toLocaleString()}`, boxY + 70);
      row("Discount", `- NGN ${Number(booking.discount_amount).toLocaleString()}`, boxY + 86);
      rowOffset = 86;
    }

    doc.setDrawColor(220, 225, 235);
    doc.line(50, boxY + rowOffset + 14, W - 50, boxY + rowOffset + 14);
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text("Total Amount", 50, boxY + rowOffset + 28);
    doc.setTextColor(...GOLD);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`NGN ${Number(booking.total_amount).toLocaleString()}`, W - 50, boxY + rowOffset + 30, { align: "right" });

    // Footer
    doc.setFillColor(...NAVY);
    doc.rect(0, H - 70, W, 70, "F");
    doc.setFillColor(...GOLD);
    doc.rect(0, H - 74, W, 4, "F");
    doc.setTextColor(...WHITE);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text("Thank you for choosing Aurelia!", W / 2, H - 48, { align: "center" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...GOLD);
    doc.text("aurelia-apartments.com  •  support@aurelia-apartments.com", W / 2, H - 30, { align: "center" });

    doc.save(`aurelia-receipt-${receiptNo}.pdf`);
  };

  return (
    <div className={styles.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      {/* Mobile overlay when sidebar is open */}
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}
      <div className={styles.content}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <div className={styles.reservations}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3>Reservations</h3>
            <button
              className={styles.manualBookBtn}
              style={{ marginLeft: 12, background: showCalendar ? '#1976d2' : undefined, color: showCalendar ? '#fff' : undefined }}
              onClick={() => setShowCalendar((v) => !v)}
            >
              {showCalendar ? 'Show Table View' : 'Show Calendar View'}
            </button>
          </div>
          {!showCalendar && (
            <>
              <div className={styles.controls}>
                <input
                  type="text"
                  placeholder="Search Room ID"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                <select
                  value={month}
                  onChange={(e) => {
                    setMonth(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  {months.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
                <select
                  value={sort}
                  onChange={(e) => {
                    setSort(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option>Most Recent</option>
                  <option>Oldest</option>
                </select>
                <button className={styles.manualBookBtn} onClick={() => setShowManualModal(true)}>
                  <span className={styles.plusIcon}>＋</span> <span>Manual Booking</span>
                </button>
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Room Type</th>
                      <th>Stay Dates</th>
                      <th>Room Type & Room ID</th>
                      <th>Total Cost</th>
                      <th>Booking Type</th>
                      <th>Guest/User Email</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.length === 0 ? (
                      <tr>
                        <td colSpan="7" className={styles.noData}>
                          No reservations found.
                        </td>
                      </tr>
                    ) : (
                      paginated.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <div className={styles.roomInfo}>
                              <img
                                src={getFirstImageUrl(row.images || (row.image ? [row.image] : []))}
                                alt="Room"
                                style={{ width: 60, height: 40, objectFit: "cover", borderRadius: 4 }}
                              />
                              <span>{row.roomType}</span>
                            </div>
                          </td>
                          <td>{row.stayDates}</td>
                          <td>
                            <strong>{row.roomType} - {row.roomNumber}</strong>
                          </td>
                          <td>{row.cost}</td>
                          <td>
                            <span className={`${styles.bookingType} ${styles[row.booking_type]}`}>
                              {row.booking_type}
                            </span>
                          </td>
                          <td>{row.guest_email || 'N/A'}</td>
                          <td>
                            <div className={styles.actions}>
                              <button className={styles.view} onClick={() => handleView(row.id)}>
                                View Details →
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {showCalendar && <BookingCalendarView />}
          <div className={styles.pagination}>
            <button
              className={styles.pageButton}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              ‹ Previous
            </button>
            {Array.from({ length: totalPages }, (_, n) => (
              <button
                key={n}
                onClick={() => setCurrentPage(n + 1)}
                className={`${styles.pageButton} ${currentPage === n + 1 ? styles.active : ''}`}
              >
                {n + 1}
              </button>
            ))}
            <button
              className={styles.pageButton}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next ›
            </button>
          </div>
        </div>
        {/* Manual Booking Modal */}
        {showManualModal && (
          <div className={styles.modalOverlay}>
            <div className={styles.modalContentStyled} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 className={styles.modalTitle}>Manual Booking</h3>

              <label className={styles.modalLabel}>Room Type *</label>
              <select
                className={styles.modalSelect}
                value={manualBooking.room_type_id}
                onChange={e => {
                  setManualBooking({
                    ...manualBooking,
                    room_type_id: e.target.value
                  });
                }}
              >
                <option value="">Select Room Type</option>
                {roomTypeOptions.map(type => (
                  <option key={type.id} value={type.id}>
                    {type.title ? `${type.title} (ID: ${type.id})` : `Type ${type.id}`}
                  </option>
                ))}
              </select>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', margin: '16px 0' }}>
                <div>
                  <label className={styles.modalLabel}>Start Date *</label>
                  <input 
                    className={styles.modalInput} 
                    type="date" 
                    value={manualBooking.start_date} 
                    onChange={e => setManualBooking({ ...manualBooking, start_date: e.target.value })} 
                  />
                </div>
                <div>
                  <label className={styles.modalLabel}>End Date *</label>
                  <input 
                    className={styles.modalInput} 
                    type="date" 
                    value={manualBooking.end_date} 
                    onChange={e => setManualBooking({ ...manualBooking, end_date: e.target.value })} 
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', margin: '16px 0' }}>
                <div>
                  <label className={styles.modalLabel}>First Name *</label>
                  <input 
                    className={styles.modalInput} 
                    type="text" 
                    value={manualBooking.guest_first_name} 
                    onChange={e => setManualBooking({ ...manualBooking, guest_first_name: e.target.value })} 
                    placeholder="First name"
                  />
                </div>
                <div>
                  <label className={styles.modalLabel}>Last Name *</label>
                  <input 
                    className={styles.modalInput} 
                    type="text" 
                    value={manualBooking.guest_last_name} 
                    onChange={e => setManualBooking({ ...manualBooking, guest_last_name: e.target.value })} 
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', margin: '16px 0' }}>
                <div>
                  <label className={styles.modalLabel}>Guest Email *</label>
                  <input 
                    className={styles.modalInput} 
                    type="email" 
                    value={manualBooking.guest_email} 
                    onChange={e => setManualBooking({ ...manualBooking, guest_email: e.target.value })} 
                    placeholder="Enter guest email"
                  />
                </div>
                <div>
                  <label className={styles.modalLabel}>Guest Phone *</label>
                  <input 
                    className={styles.modalInput} 
                    type="text" 
                    value={manualBooking.guest_phone} 
                    onChange={e => setManualBooking({ ...manualBooking, guest_phone: e.target.value })} 
                    placeholder="Enter guest phone"
                  />
                </div>
              </div>

              {/* Airport Conveyance Section */}
              <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#495057' }}>Airport Conveyance (Optional)</h4>
                <label className={styles.modalLabel}>Service Type</label>
                <select
                  className={styles.modalSelect}
                  value={manualBooking.airport_direction}
                  onChange={e => setManualBooking({ ...manualBooking, airport_direction: e.target.value })}
                >
                  <option value="none">No airport service</option>
                  <option value="pickup">Pickup only (₦40,000)</option>
                  <option value="dropoff">Drop-off only (₦40,000)</option>
                  <option value="both">Both pickup & drop-off (₦70,000)</option>
                </select>

                {/* Pickup Fields */}
                {(manualBooking.airport_direction === 'pickup' || manualBooking.airport_direction === 'both') && (
                  <div style={{ marginTop: '16px' }}>
                    <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#28a745' }}>Pickup Details</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <input 
                        className={styles.modalInput}
                        value={manualBooking.pickup_flight_number}
                        onChange={e => setManualBooking({ ...manualBooking, pickup_flight_number: e.target.value })}
                        placeholder="Flight Number" 
                        style={{ fontSize: '13px' }}
                      />
                      <input 
                        className={styles.modalInput}
                        value={manualBooking.pickup_airline}
                        onChange={e => setManualBooking({ ...manualBooking, pickup_airline: e.target.value })}
                        placeholder="Airline" 
                        style={{ fontSize: '13px' }}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <input 
                        className={styles.modalInput}
                        type="datetime-local"
                        value={manualBooking.pickup_arrival_time}
                        onChange={e => setManualBooking({ ...manualBooking, pickup_arrival_time: e.target.value })}
                        style={{ fontSize: '13px' }}
                      />
                      <input 
                        className={styles.modalInput}
                        value={manualBooking.pickup_terminal}
                        onChange={e => setManualBooking({ ...manualBooking, pickup_terminal: e.target.value })}
                        placeholder="Terminal" 
                        style={{ fontSize: '13px' }}
                      />
                    </div>
                  </div>
                )}

                {/* Drop-off Fields */}
                {(manualBooking.airport_direction === 'dropoff' || manualBooking.airport_direction === 'both') && (
                  <div style={{ marginTop: '16px' }}>
                    <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#ff6b35' }}>Drop-off Details</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                      <input 
                        className={styles.modalInput}
                        value={manualBooking.dropoff_flight_number}
                        onChange={e => setManualBooking({ ...manualBooking, dropoff_flight_number: e.target.value })}
                        placeholder="Flight Number" 
                        style={{ fontSize: '13px' }}
                      />
                      <input 
                        className={styles.modalInput}
                        value={manualBooking.dropoff_airline}
                        onChange={e => setManualBooking({ ...manualBooking, dropoff_airline: e.target.value })}
                        placeholder="Airline" 
                        style={{ fontSize: '13px' }}
                      />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                      <input 
                        className={styles.modalInput}
                        type="datetime-local"
                        value={manualBooking.dropoff_departure_time}
                        onChange={e => setManualBooking({ ...manualBooking, dropoff_departure_time: e.target.value })}
                        style={{ fontSize: '13px' }}
                      />
                      <input 
                        className={styles.modalInput}
                        value={manualBooking.dropoff_terminal}
                        onChange={e => setManualBooking({ ...manualBooking, dropoff_terminal: e.target.value })}
                        placeholder="Terminal" 
                        style={{ fontSize: '13px' }}
                      />
                      <input 
                        className={styles.modalInput}
                        type="datetime-local"
                        value={manualBooking.dropoff_pickup_time}
                        onChange={e => setManualBooking({ ...manualBooking, dropoff_pickup_time: e.target.value })}
                        placeholder="Hotel Pickup Time" 
                        style={{ fontSize: '13px' }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Breakfast Section */}
              <div style={{ marginTop: '24px', padding: '20px', background: '#fff8e1', borderRadius: '8px', border: '1px solid #ffcc02' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <input
                    type="checkbox"
                    id="breakfast"
                    checked={manualBooking.breakfast}
                    onChange={e => setManualBooking({ ...manualBooking, breakfast: e.target.checked })}
                    style={{ transform: 'scale(1.2)' }}
                  />
                  <label htmlFor="breakfast" style={{ fontSize: '16px', fontWeight: '600', color: '#f57c00' }}>
                    Include Breakfast (₦6,000 per person per night)
                  </label>
                </div>
                {manualBooking.breakfast && (
                  <div>
                    <label className={styles.modalLabel} style={{ color: '#f57c00' }}>Number of People</label>
                    <select
                      className={styles.modalSelect}
                      value={manualBooking.breakfast_count}
                      onChange={e => setManualBooking({ ...manualBooking, breakfast_count: e.target.value })}
                      style={{ width: '150px', border: '1px solid #ffcc02' }}
                    >
                      <option value="1">1 person</option>
                      <option value="2">2 people</option>
                      <option value="3">3 people</option>
                      <option value="4">4 people</option>
                    </select>
                  </div>
                )}
              </div>

              {/* Price Adjustment */}
              <div style={{ marginTop: '24px', padding: '20px', background: '#f0f4ff', borderRadius: '8px', border: '1px solid #c3d0f5' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#233d81' }}>Price Adjustment (Optional)</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label className={styles.modalLabel}>Discount Amount (NGN)</label>
                    <input
                      className={styles.modalInput}
                      type="number"
                      min="0"
                      step="100"
                      value={manualBooking.discount_amount}
                      onChange={e => setManualBooking({ ...manualBooking, discount_amount: e.target.value, discount_type: 'fixed' })}
                      placeholder={'e.g. 5000'}
                    />
                  </div>
                </div>
                <label className={styles.modalLabel}>Reason for Discount</label>
                <input
                  className={styles.modalInput}
                  type="text"
                  value={manualBooking.discount_reason}
                  onChange={e => setManualBooking({ ...manualBooking, discount_reason: e.target.value })}
                  placeholder="e.g. Loyalty discount, Promotional offer"
                />
              </div>

              {/* Affiliate Code */}
              <div style={{ marginTop: '24px' }}>
                <label className={styles.modalLabel}>Affiliate Code (Optional)</label>
                <input 
                  className={styles.modalInput} 
                  type="text" 
                  value={manualBooking.affiliate_code} 
                  onChange={e => setManualBooking({ ...manualBooking, affiliate_code: e.target.value })} 
                  placeholder="Enter affiliate code if applicable"
                />
              </div>

              {manualError && <div className={styles.error} style={{ marginTop: '16px' }}>{manualError}</div>}
              {manualSuccess && <div className={styles.success} style={{ marginTop: '16px' }}>{manualSuccess}</div>}
              
              <div className={styles.modalActions} style={{ marginTop: '32px' }}>
                <button className={styles.confirmBtnStyled} onClick={handleManualBooking}>Confirm Booking</button>
                <button className={styles.cancelBtnStyled} onClick={() => setShowManualModal(false)}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingManagement;
